import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdViewSegment from "./components/AdViewSegment";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { fetchAds, blockAd, activateAds } from "../../../services/ads";
import { AdsResponse, AdStatus } from '../../../types/api/ads';
import { useAlert } from '../../../context/alert';
import { crossTabCommunication } from '../../../hooks/useCrossTabCommunication';
import { formatAddress } from '../../../helpers/address';
import { isActiveAdStatus } from '../../../types/api/ads';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import { CreateAdsCommentModal } from '../../ads/Components/CreateAdsCommentModal';
import { DisplayAdsCommentModal } from '../../ads/Components/DisplayAdsCommentModal';
import { StatsCard } from "../../basic/StatsCard";
import AdsInfoHeader from '../../user/Ads/components/AdsInfoHeader';
import AdDescrSection from './components/AdDescrSection';
import InfoSection from '../../basic/InfoSection';
import TableActionsBanner from '../../common/TableActionsBanner';
import InfoActionsBanner from '../../common/InfoActionsBanner';
import { Stack, Button, Text } from '@primer/react';
import { CopyIcon, NoteIcon, CommentIcon, CheckIcon, BlockedIcon } from '@primer/octicons-react';
import { isAllowAction } from '../../../services/permissions/utils';
import { useUserContext } from '../../../context/user';

export default function AdView() {

    const { profile } = useUserContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const fetchedIdRef = useRef<string | null>(null);
    const [ad, setAd] = useState<AdsResponse | undefined>(undefined);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();
    const [openCreateCommentModal, setOpenCreateCommentModal] = React.useState(false)
    const [openDisplayCommentModal, setOpenDisplayCommentModal] = React.useState(false)

    const { mutate: fetchAdById, isPending: isLoadingAd } = useMutation({
        mutationFn: fetchAds,
        onSuccess: (data) => {
            markAdAsViewed()
            setAd(data)
        },
        onError: (error: any) => {

        }
    });

    useEffect(() => {
        const id = searchParams.get('id');
        if (id && fetchedIdRef.current !== id && !isLoadingAd) {
            fetchedIdRef.current = id;
            fetchAdById(id);
        }
    }, [searchParams, fetchAdById, isLoadingAd]);

    const markAdAsViewed = async () => {
        crossTabCommunication.sendMessage('LINK_VIEWED', { linkId: fetchedIdRef.current });
    };

    const fetchAd = () => {
        const id = searchParams.get('id');
        if (id) {
            fetchAdById(id);
        }
    }

    const { mutate: blockAds } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => blockAd([id]),
        onSuccess: (data) => {
            fetchAd()
            showSuccessBlockAlert()
        }
    });

    const { mutate: allowAds } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => activateAds([id]),
        onSuccess: (data) => {
            fetchAd()
            showSuccessConfirmAlert()
        }
    });

    const openFaqPage = () => {
        const adId = ad?.id
        if (adId) {
            navigate('/ads/faq?id=' + adId)
        }
    }

    const openUserPage = () => {
        const userId = ad?.user?.id
        if (userId) {
            navigate('/users/user?id=' + userId)
        }
    }

    const showCreateCommentForAd = () => {
        setActionsAlertId(undefined)
        dismissConfirmBlockAlert()
        setOpenCreateCommentModal(true)
    }

    const commentCreateModalClosed = () => {
        setOpenCreateCommentModal(false)
    }

    const blockCurrentAd = (ad: AdsResponse) => {
        // ALX-60743: блокировка одна для ожидающих и подтверждённых, как по ТЗ
        switch (ad.status) {
            case "WAITING":
            case "CONFIRMED":
                blockAds({ id: ad.id })
                break
            default:
                break
        }
    }

    const allowCurrentAd = (ad: AdsResponse) => {
        allowAds({ id: ad.id })
    }

    function createConfirmBlockAlertProps(ad: AdsResponse) {
        return {
            title: 'Точно хотите заблокировать объявление?',
            count: undefined,
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmBlockAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={() => blockCurrentAd(ad)}>
                    Да, заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    // CONFIRM SINGLE AD
    function createConfirmOneAdAlertProps(ad: AdsResponse) {
        return {
            title: 'Точно хотите подтвердить объявление?',
            count: undefined,
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissConfirmBlockAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={() => allowCurrentAd(ad)}>
                    Да, подтвердить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    function createSuccessBlockAlertProps() {
        return {
            title: 'Объявление заблокировано',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showCreateCommentForAd}>
                    Оставить комментарий
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    // Плашка блокировки без автозакрытия: уходя с карточки, гасим её сами,
    // иначе она остаётся висеть поверх других экранов (ALX-60503)
    useEffect(() => {
        return () => {
            const alertId = actionsAlertIdRef.current
            if (alertId) {
                removeAlert(alertId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const dismissConfirmBlockAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);

    const showSuccessBlockAlert = useCallback(() => {
        const props = createSuccessBlockAlertProps()
        //showUpdatedAlert(props)
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        const alertId = addCustomAlert(
            InfoActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    const showSuccessConfirmAlert = useCallback(() => {
        const props = createSuccessBlockAlertProps()
        //showUpdatedAlert(props)
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        setInfo('Объявление подтверждено')
    }, [actionsAlertId]);

    const showConfirmBlockAlert = useCallback((ad: AdsResponse) => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        let props = createConfirmBlockAlertProps(ad)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId, ad]);

    const showAllowAdAlert = useCallback((ad: AdsResponse) => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()

        let props = createConfirmOneAdAlertProps(ad)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId

    }, [actionsAlertId]);

    const showCommentForDeletedAds = () => {
        setOpenDisplayCommentModal(true)
    }

    const commentDisplayModalClosed = () => {
        setOpenDisplayCommentModal(false)
    }

    const commentCreateModalSended = () => {
        setOpenCreateCommentModal(false)
        fetchAd()
    }

    const detailRows = [
        {
            title: 'Описание',
            value: ad?.description ?? '',
            isAllowCopy: false
        },
        {
            title: 'Адрес',
            value: formatAddress(ad?.address),
            isAllowCopy: true
        }
    ];

    const renderActionButtons = () => {
        if (ad) {
            if (isActiveAdStatus(ad.status)) {
                switch (ad.status) {
                    case AdStatus.WAITING:
                        const confirm = (<Button
                            key="confirm-button"
                            variant="default"
                            leadingVisual={CheckIcon}
                            onClick={() => showAllowAdAlert(ad)}
                        >
                            Подтвердить
                        </Button>)
                        const block = (<Button
                            key="block-button"
                            variant="default"
                            leadingVisual={BlockedIcon}
                            onClick={() => showConfirmBlockAlert(ad)}
                        >
                            Заблокировать
                        </Button>)

                        return (
                            <>
                                {isAllowAction(profile, 'APPROVE_ADS') && confirm}
                                {isAllowAction(profile, 'BLOCK_ADS') && block}
                            </>
                        );
                    case AdStatus.CONFIRMED:
                    case AdStatus.REJECTED:
                    case AdStatus.EXPIRED:
                    case AdStatus.CLOSED:
                        return isAllowAction(profile, 'BLOCK_ADS') && (
                            < Button
                                key="block-button"
                                variant="default"
                                leadingVisual={BlockedIcon}
                                onClick={() => showConfirmBlockAlert(ad)}
                            >
                                Заблокировать
                            </Button >
                        );
                }
            } else {
                return (
                    ad.hasAdminComments && <Button
                        key="comment-button"
                        variant="default"
                        leadingVisual={CommentIcon}
                        onClick={showCommentForDeletedAds}
                    >
                        Комментарий
                    </Button>
                );
            }
        };
        return null;
    }

    return (
        <RegularPageLayout
            header={
                <AdViewSegment id={searchParams.get('id') || ''} />
            }
            content={(
                <Stack className='mt-6'>
                    {(ad) &&
                        <Stack className='mt-6 mx-auto'>

                            {ad.hasAdminComments && <DisplayAdsCommentModal
                                isOpen={openDisplayCommentModal}
                                ads={ad}
                                comment={"ad.hasAdminComments"}
                                onClose={commentDisplayModalClosed} />}

                            <CreateAdsCommentModal
                                isOpen={openCreateCommentModal}
                                adIds={[ad.id]}
                                onDidCommentSended={commentCreateModalSended}
                                onClose={commentCreateModalClosed}
                            />

                            <Stack justify="space-between" direction="horizontal" className='mb-7'>

                                <AdsInfoHeader
                                    className=''
                                    ads={ad}
                                    imageSize={80}
                                    nameFontSize={20}
                                />

                                <Stack direction="horizontal" align="center">
                                    {renderActionButtons()}
                                </Stack>
                            </Stack>

                            <Stack
                                className='mb-7'
                                direction="horizontal"
                                align="center">
                                <StatsCard
                                    title="Просмотры"
                                    count={ad.viewsCount}
                                    isAllowClick={false}
                                    renderValue={() => (
                                        <Stack align="center" direction="horizontal" gap="none">
                                            <Text
                                                as="span"
                                                weight='medium'
                                                className='text-[20px] text-aloix-black whitespace-pre-line'>
                                                {ad.viewsCount}
                                            </Text>
                                            <Text as="span" weight='normal' className='ml-[6px] text-[14px] text-aloix-grey whitespace-pre-line'>
                                                ( {ad.todayViewsCount} сегодня)
                                            </Text>
                                        </Stack>
                                    )
                                    }
                                />
                                <StatsCard
                                    title="Комментарии"
                                    count={ad.commentsCount}
                                    isAllowClick={true}
                                    onClick={openFaqPage} />
                                <StatsCard
                                    title="В избранном"
                                    count={ad.favouritesCount}
                                    isAllowClick={false} />
                            </Stack>

                            <AdDescrSection
                                className='border-t border-b border-separator pt-7 pb-7'
                                ads={ad}
                                onUserClick={openUserPage}
                            />
                            <InfoSection
                                className='mt-7'
                                title="Детали"
                                rows={detailRows}
                                HeaderIcon={NoteIcon}
                                CopyIcon={CopyIcon}
                                Text={Text}
                            />
                        </Stack>
                    }
                </Stack>
            )}
        />
    )
}